import React, { useState, useCallback } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useAppDispatch } from "@app-redux/store";
import { createOpportunity } from "@app-redux/thunk/opportunityThunk";
import type { ColumnDef, OpportunityField } from "./columnConfig";
import styles from "./styles.module.css";

type Props = {
  categoryId: string;
  onCancel: () => void;
  columns: ColumnDef[];
};

type FormValues = Record<OpportunityField, string | null>;

const INITIAL_VALUES: FormValues = {
  first_us_date: null,
  artist_tour: "",
  ld: "",
  previous_tour_ld: "",
  christie_rep_ld: "",
  production_manager: "",
  previous_tour_pm: "",
  christie_rep_pm: "",
  band_management: "",
  location: "",
  agency: "",
  producer: "",
};

export const AddOpportunityRow = ({ categoryId, onCancel, columns }: Props) => {
  const dispatch = useAppDispatch();
  const [formValues, setFormValues] = useState<FormValues>({ ...INITIAL_VALUES });

  const handleChange = useCallback(
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
    },
    []
  );

  const handleDateChange = useCallback(
    (field: string) => (newVal: Dayjs | null) => {
      setFormValues((prev) => ({
        ...prev,
        [field]: newVal ? newVal.toISOString() : null,
      }));
    },
    []
  );

  const handleSave = useCallback(() => {
    dispatch(
      createOpportunity({
        category_id: categoryId,
        ...formValues,
      } as Parameters<typeof createOpportunity>[0])
    );
    onCancel();
  }, [dispatch, categoryId, formValues, onCancel]);

  return (
    <tr>
      {columns.map((col) => (
        <td key={col.field}>
          {col.isDate ? (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={
                  formValues[col.field]
                    ? dayjs(formValues[col.field])
                    : null
                }
                onChange={handleDateChange(col.field)}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "standard",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          ) : (
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={formValues[col.field] ?? ""}
              onChange={handleChange(col.field)}
            />
          )}
        </td>
      ))}
      <td className={styles.actionsCell}>
        <IconButton size="small" onClick={handleSave} color="primary">
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onCancel}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </td>
    </tr>
  );
};
